import { motion, useScroll, useTransform } from "framer-motion";
import { Star, Quote, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { useRef } from "react";

const testimonials = [
  {
    id: 1,
    name: "Alessandro Moretti",
    title: "CEO, Moretti Investments",
    location: "Milan, Italy",
    image: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=150&h=150&fit=crop&crop=face",
    quote: "Agota Estate demonstrated exceptional professionalism throughout our Monaco acquisition. Their thorough due diligence and meticulous attention to every contractual detail made a complex cross-border transaction remarkably smooth.",
    rating: 5,
    investment: "€8.2M Portfolio"
  },
  {
    id: 2,
    name: "Victoria Lindström",
    title: "Private Investor",
    location: "Stockholm, Sweden",
    image: "https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=150&h=150&fit=crop&crop=face",
    quote: "What sets Agota Estate apart is their deep understanding of client needs and market dynamics. They identified a Marbella villa that appreciated 40% in two years. Their global network and local expertise are unmatched.",
    rating: 5,
    investment: "€5.5M Portfolio"
  },
  {
    id: 3,
    name: "Omar Al-Rashid",
    title: "Managing Director, Gulf Capital",
    location: "Dubai, UAE",
    image: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=150&h=150&fit=crop&crop=face",
    quote: "Agota Estate guided our Dubai expansion with confidence and precision. Their expertise in handling complex cross-border regulations and their ability to navigate different business cultures made them invaluable partners.",
    rating: 5,
    investment: "€12M Portfolio"
  },
  {
    id: 4,
    name: "Isabelle Fontaine",
    title: "Art Collector & Philanthropist",
    location: "Paris, France",
    image: "https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=150&h=150&fit=crop&crop=face",
    quote: "Agota Estate doesn't just find properties — they understand the lifestyle you're seeking. Their comprehensive approach across multiple markets is impressive. The Tuscan villa they found perfectly balances privacy with accessibility.",
    rating: 5,
    investment: "€6.8M Portfolio"
  },
  {
    id: 5,
    name: "Henrik Johansson",
    title: "Founder, Nordic Ventures",
    location: "Oslo, Norway",
    image: "https://images.unsplash.com/photo-1560250097-0b93528c311a?w=150&h=150&fit=crop&crop=face",
    quote: "The level of market insight Agota Estate provides is exceptional. They anticipated market movements in Monaco that resulted in substantial capital appreciation for our portfolio.",
    rating: 5,
    investment: "€9.4M Portfolio"
  },
  {
    id: 6,
    name: "Sophia Castellano",
    title: "Fashion Industry Executive",
    location: "London, UK",
    image: "https://images.unsplash.com/photo-1580489944761-15a19d654956?w=150&h=150&fit=crop&crop=face",
    quote: "Agota Estate's white-glove service is unparalleled. From property selection to interior design coordination, they managed every aspect of my Marbella purchase with impeccable taste.",
    rating: 5,
    investment: "€4.2M Portfolio"
  }
];

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.15,
      delayChildren: 0.1,
    },
  },
};

const itemVariants = {
  hidden: { 
    opacity: 0, 
    y: 40,
    scale: 0.95,
  },
  visible: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: {
      type: "spring" as const,
      stiffness: 100,
      damping: 15,
    },
  },
};

const TestimonialsSection = () => {
  const sectionRef = useRef<HTMLDivElement>(null);
  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ["start end", "end start"],
  });

  // Parallax effects
  const bgY1 = useTransform(scrollYProgress, [0, 1], [80, -80]);
  const bgY2 = useTransform(scrollYProgress, [0, 1], [-60, 60]);
  const bgOpacity = useTransform(scrollYProgress, [0, 0.3, 0.7, 1], [0, 0.4, 0.4, 0]);
  const quoteY = useTransform(scrollYProgress, [0, 1], [40, -40]);

  return (
    <section 
      ref={sectionRef}
      className="relative py-24 bg-gradient-to-b from-background to-secondary/30 overflow-hidden"
    >
      {/* Parallax Background Elements */}
      <motion.div 
        className="absolute inset-0 pointer-events-none"
        style={{ opacity: bgOpacity }}
      >
        <motion.div 
          className="absolute top-40 -right-32 w-80 h-80 rounded-full bg-gradient-to-bl from-gold/15 to-transparent blur-3xl"
          style={{ y: bgY1 }}
        />
        <motion.div 
          className="absolute bottom-40 -left-32 w-80 h-80 rounded-full bg-gradient-to-tr from-primary/10 to-transparent blur-3xl"
          style={{ y: bgY2 }}
        />
        {/* Decorative floating quote */}
        <motion.div
          className="absolute top-1/4 left-1/4 text-primary/5"
          style={{ y: quoteY }}
        >
          <Quote className="w-48 h-48" />
        </motion.div>
      </motion.div>

      <div className="container mx-auto px-4 relative z-10">
        {/* Header with enhanced animation */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true, margin: "-100px" }}
          className="text-center mb-16"
        >
          <motion.span 
            className="text-primary font-medium tracking-widest uppercase text-sm inline-block"
            initial={{ opacity: 0, y: -10 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            viewport={{ once: true }}
          >
            Client Perspectives
          </motion.span>
          <h2 className="font-display text-4xl md:text-5xl text-foreground mt-4 mb-6">
            Trusted by Discerning Investors
          </h2>
          <p className="text-muted-foreground max-w-2xl mx-auto text-lg">
            Distinguished portfolios built through discretion, deep market intelligence, 
            and an unwavering commitment to preserving generational wealth.
          </p>
        </motion.div>

        {/* Testimonials Grid with stagger animation */}
        <motion.div 
          className="grid md:grid-cols-2 gap-8 max-w-6xl mx-auto"
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-50px" }}
        >
          {testimonials.slice(0, 4).map((testimonial, idx) => (
            <motion.div
              key={testimonial.id}
              variants={itemVariants}
              className="group"
            >
              <motion.div 
                className="relative bg-card border border-border/50 rounded-2xl p-8 h-full transition-all duration-500 hover:border-primary/30 hover:shadow-elegant"
                whileHover={{ 
                  y: -8, 
                  scale: 1.02,
                }}
                transition={{ duration: 0.3 }}
              >
                {/* Quote Icon with animation */}
                <motion.div 
                  className="absolute -top-4 left-8"
                  initial={{ opacity: 0, scale: 0, rotate: -20 }}
                  whileInView={{ opacity: 1, scale: 1, rotate: 0 }}
                  transition={{ delay: 0.3 + idx * 0.1, type: "spring" as const, stiffness: 200 }}
                  viewport={{ once: true }}
                >
                  <div className="w-10 h-10 bg-primary rounded-full flex items-center justify-center shadow-lg">
                    <Quote className="w-5 h-5 text-primary-foreground" />
                  </div>
                </motion.div>

                {/* Rating with staggered stars */}
                <div className="flex gap-1 mb-6 pt-2">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <motion.div
                      key={i}
                      initial={{ opacity: 0, scale: 0 }}
                      whileInView={{ opacity: 1, scale: 1 }}
                      transition={{ delay: 0.4 + i * 0.1, type: "spring" as const, stiffness: 200 }}
                      viewport={{ once: true }}
                    >
                      <Star className="w-4 h-4 fill-primary text-primary" />
                    </motion.div>
                  ))}
                </div>

                {/* Quote */}
                <blockquote className="text-foreground/90 leading-relaxed mb-8 italic">
                  "{testimonial.quote}"
                </blockquote>

                {/* Client Info */}
                <div className="flex items-center gap-4">
                  <motion.div 
                    className="relative"
                    whileHover={{ scale: 1.1 }}
                    transition={{ duration: 0.3 }}
                  >
                    <img
                      src={testimonial.image}
                      alt={testimonial.name}
                      className="w-14 h-14 rounded-full object-cover border-2 border-primary/20"
                    />
                    <motion.div 
                      className="absolute -bottom-1 -right-1 w-5 h-5 bg-green-500 rounded-full border-2 border-card"
                      initial={{ scale: 0 }}
                      whileInView={{ scale: 1 }}
                      transition={{ delay: 0.5, type: "spring" as const }}
                      viewport={{ once: true }}
                    />
                  </motion.div>
                  <div className="flex-1">
                    <h4 className="font-display text-lg text-foreground">
                      {testimonial.name}
                    </h4>
                    <p className="text-muted-foreground text-sm">
                      {testimonial.title}
                    </p>
                    <p className="text-primary/80 text-xs mt-0.5">
                      {testimonial.location}
                    </p>
                  </div>
                  <div className="text-right">
                    <span className="text-xs text-muted-foreground uppercase tracking-wider">
                      Investment
                    </span>
                    <p className="font-display text-primary font-semibold">
                      {testimonial.investment}
                    </p>
                  </div>
                </div>
              </motion.div>
            </motion.div>
          ))}
        </motion.div>

        {/* View All Button */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.3 }}
          viewport={{ once: true }}
          className="mt-12 text-center"
        >
          <Button variant="outline" size="lg" asChild>
            <Link to="/testimonials" className="inline-flex items-center gap-2">
              View All Client Stories
              <ArrowRight size={18} />
            </Link>
          </Button>
        </motion.div>

        {/* Trust Indicators with stagger animation */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.4 }}
          viewport={{ once: true }}
          className="mt-12 text-center"
        >
          <motion.div 
            className="inline-flex flex-wrap justify-center items-center gap-4 sm:gap-8 bg-card/50 backdrop-blur-sm border border-border/30 rounded-full px-6 sm:px-8 py-4"
            whileHover={{ scale: 1.02 }}
            transition={{ duration: 0.3 }}
          >
            <motion.div 
              className="text-center"
              initial={{ opacity: 0, scale: 0.5 }}
              whileInView={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.5, type: "spring" as const }}
              viewport={{ once: true }}
            >
              <span className="font-display text-2xl text-primary font-semibold block">€65M+</span>
              <p className="text-xs text-muted-foreground uppercase tracking-wider">Total Investments</p>
            </motion.div>
            <div className="w-px h-10 bg-border/50 hidden sm:block" />
            <motion.div 
              className="text-center"
              initial={{ opacity: 0, scale: 0.5 }}
              whileInView={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.6, type: "spring" as const }}
              viewport={{ once: true }}
            >
              <span className="font-display text-2xl text-primary font-semibold block">100%</span>
              <p className="text-xs text-muted-foreground uppercase tracking-wider">Client Satisfaction</p>
            </motion.div>
            <div className="w-px h-10 bg-border/50 hidden sm:block" />
            <motion.div 
              className="text-center"
              initial={{ opacity: 0, scale: 0.5 }}
              whileInView={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.7, type: "spring" as const }}
              viewport={{ once: true }}
            >
              <span className="font-display text-2xl text-primary font-semibold block">8</span>
              <p className="text-xs text-muted-foreground uppercase tracking-wider">Countries</p>
            </motion.div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

export default TestimonialsSection;
